import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';

type ChartData = {
  label: string;
  value: number;
  color: string;
  index?: number;
  groupedEntries?: Array<{ label: string; value: number }>;
};

type Props = {
  data: ChartData[];
  width?: number;
  height?: number;
};

export const OverLappingPieChart: React.FC<Props> = ({
  data,
  width = 400,
  height = 350,
}) => {
  const ref = useRef<SVGSVGElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const tooltipArrowRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ref.current) return;

    const baseRadius = 62;
    const radiusStep = 14;
    const LABEL_LINE_OFFSET = 28;
    const LABEL_TEXT_OFFSET = 33;
    const POP_COLORS = [
      '#FF6B35',
      '#00C2A8',
      '#5B8DEF',
      '#FFB400',
      '#E64980',
      '#22C55E',
      '#7C3AED',
      '#F43F5E',
    ];
    const positiveData = data.filter((item) => item.value > 0);
    const TOP_ENTRIES = 3;
    const sortedData = [...positiveData].sort((a, b) => b.value - a.value);
    const topData = sortedData.slice(0, TOP_ENTRIES);
    const othersData = sortedData.slice(TOP_ENTRIES);
    const othersValue = othersData.reduce((sum, item) => sum + item.value, 0);
    const displayData: ChartData[] =
      othersValue > 0
        ? [
            ...topData,
            {
              label: `Others (${othersData.length})`,
              value: Number(othersValue.toFixed(2)),
              color: '',
              groupedEntries: othersData.map((item) => ({
                label: item.label,
                value: item.value,
              })),
            },
          ]
        : topData;
    const isValidColor = (value?: string) =>
      !!value && d3.color(value) !== null;
    const usedColors = new Set(
      displayData
        .map((item) => (isValidColor(item.color) ? item.color : undefined))
        .filter((color): color is string => !!color)
    );
    let popColorIndex = 0;
    const getNextPopColor = () => {
      while (popColorIndex < POP_COLORS.length) {
        const candidate = POP_COLORS[popColorIndex++];
        if (!usedColors.has(candidate)) {
          usedColors.add(candidate);
          return candidate;
        }
      }
      // If palette is exhausted, cycle with deterministic fallback.
      const candidate =
        POP_COLORS[(popColorIndex++ - POP_COLORS.length) % POP_COLORS.length];
      return candidate;
    };
    const finalDisplayData = displayData.map((item) => ({
      ...item,
      color: isValidColor(item.color) ? item.color : getNextPopColor(),
    }));
    const canvasWidth = width;
    const canvasHeight = height;
    const centerX = width / 2;
    const centerY = height / 2;
    const MAX_LABEL_CHARS = 8;
    const formatPercent = (value: number) =>
      Number.isInteger(value) ? `${value}` : `${value.toFixed(1)}`;
    const escapeHtml = (value: string) =>
      value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    const truncateLabel = (label: string) =>
      label.length > MAX_LABEL_CHARS
        ? `${label.slice(0, MAX_LABEL_CHARS)}...`
        : label;

    const svg = d3.select(ref.current);
    const tooltip = d3.select(tooltipRef.current);
    const tooltipArrow = d3.select(tooltipArrowRef.current);
    svg.selectAll('*').remove();

    const updateTooltipPosition = (event: MouseEvent) => {
      if (!wrapperRef.current || !tooltipRef.current) return;

      const wrapperRect = wrapperRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const activeColor = tooltip.attr('data-color') || '#1F1F1F';

      let left = event.clientX - wrapperRect.left + 10;
      let top = event.clientY - wrapperRect.top - tooltipRect.height - 10;
      let placeAbove = true;

      if (left + tooltipRect.width > wrapperRect.width) {
        left = wrapperRect.width - tooltipRect.width - 8;
      }
      if (left < 0) {
        left = 8;
      }
      if (top < 0) {
        top = event.clientY - wrapperRect.top + 10;
        placeAbove = false;
      }

      tooltip.style('left', `${left}px`).style('top', `${top}px`);

      if (placeAbove) {
        tooltipArrow
          .style('display', 'block')
          .style('left', '50%')
          .style('top', '100%')
          .style('transform', 'translateX(-50%)')
          .style('border-left', '6px solid transparent')
          .style('border-right', '6px solid transparent')
          .style('border-top', `7px solid ${activeColor}`)
          .style('border-bottom', '0 solid transparent');
      } else {
        tooltipArrow
          .style('display', 'block')
          .style('left', '50%')
          .style('top', '-7px')
          .style('transform', 'translateX(-50%)')
          .style('border-left', '6px solid transparent')
          .style('border-right', '6px solid transparent')
          .style('border-top', '0 solid transparent')
          .style('border-bottom', `7px solid ${activeColor}`);
      }
    };

    const showTooltip = (event: MouseEvent, d: d3.PieArcDatum<ChartData>) => {
      const tooltipBg = `linear-gradient(rgba(0, 0, 0, 0.18), rgba(0, 0, 0, 0.18)), ${d.data.color}`;
      const tooltipHtml = d.data.groupedEntries?.length
        ? `<div style="font-weight:700;margin-bottom:6px;">${escapeHtml(
            d.data.label
          )} - ${formatPercent(d.data.value)}%</div>${d.data.groupedEntries
            .map(
              (entry) =>
                `<div style="display:flex;justify-content:space-between;gap:10px;"><span>${escapeHtml(
                  entry.label
                )}</span><span>${formatPercent(entry.value)}%</span></div>`
            )
            .join('')}`
        : `<div style="font-weight:700;">${escapeHtml(
            d.data.label
          )} - ${formatPercent(d.data.value)}%</div>`;

      tooltip
        .style('display', 'block')
        .style('background', tooltipBg)
        .attr('data-color', d.data.color)
        .html(tooltipHtml);

      updateTooltipPosition(event);
    };

    const hideTooltip = () => {
      tooltip.style('display', 'none');
      tooltipArrow.style('display', 'none');
    };

    svg
      .attr('width', canvasWidth)
      .attr('height', canvasHeight)
      .attr('viewBox', `0 0 ${canvasWidth} ${canvasHeight}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto')
      .style('display', 'block');

    if (!finalDisplayData.length) {
      hideTooltip();
      svg
        .append('text')
        .attr('x', canvasWidth / 2)
        .attr('y', canvasHeight / 2)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .style('fill', '#6B7280')
        .style('font-weight', '600')
        .style('font-size', '12px')
        .text('No data available');
      return;
    }

    const g = svg
      .append('g')
      .attr('transform', `translate(${centerX}, ${centerY})`);

    const pie = d3
      .pie<ChartData>()
      .sort(null)
      .value((d) => d.value);

    const arcs = pie(finalDisplayData);

    // Draw in order (small -> large).
    g.selectAll('path')
      .data(arcs)
      .enter()
      .append('path')
      .attr('d', (d, i) => {
        const arc = d3
          .arc<d3.PieArcDatum<ChartData>>()
          .innerRadius(0)
          .outerRadius(baseRadius + i * radiusStep);

        return arc(d)!;
      })
      .attr('fill', (d) => d.data.color)
      .attr('stroke', 'transparent')
      .attr('stroke-width', 2)
      .on('mouseenter', function (event, d) {
        showTooltip(event as MouseEvent, d);
      })
      .on('mousemove', function (event) {
        updateTooltipPosition(event as MouseEvent);
      })
      .on('mouseleave', hideTooltip);

    // ---------- LABELS ----------
    const labelArc = d3
      .arc<d3.PieArcDatum<ChartData>>()
      .innerRadius(baseRadius + finalDisplayData.length * radiusStep + 6)
      .outerRadius(baseRadius + finalDisplayData.length * radiusStep + 16);

    const labels = g.selectAll('.label').data(arcs).enter();

    labels
      .append('polyline')
      .attr('points', (d) => {
        const pos = labelArc.centroid(d);
        const mid = (d.startAngle + d.endAngle) / 2;

        pos[0] = (baseRadius + LABEL_LINE_OFFSET) * (mid < Math.PI ? 1 : -1);

        const tempArc = d3
          .arc<d3.PieArcDatum<ChartData>>()
          .innerRadius(0)
          .outerRadius(baseRadius + 10);

        const points = [tempArc.centroid(d), labelArc.centroid(d), pos];

        return points.map((p) => p.join(',')).join(' ');
      })
      .attr('fill', 'none')
      .attr('stroke', (d) => d.data.color)
      .attr('stroke-width', 1.5);

    labels
      .append('text')
      .attr('transform', (d) => {
        const pos = labelArc.centroid(d);
        const mid = (d.startAngle + d.endAngle) / 2;

        pos[0] = (baseRadius + LABEL_TEXT_OFFSET) * (mid < Math.PI ? 1 : -1);

        return `translate(${pos})`;
      })
      .style('text-anchor', (d) => {
        const mid = (d.startAngle + d.endAngle) / 2;
        return mid < Math.PI ? 'start' : 'end';
      })
      .on('mouseenter', function (event, d) {
        showTooltip(event as MouseEvent, d);
      })
      .on('mousemove', function (event) {
        updateTooltipPosition(event as MouseEvent);
      })
      .on('mouseleave', hideTooltip)
      .each(function (d) {
        const text = d3.select(this);

        text
          .append('tspan')
          .text(truncateLabel(d.data.label))
          .attr('x', 0)
          .attr('dy', '0em')
          .style('fill', d.data.color)
          .style('font-weight', '600')
          .style('font-size', '10px');

        text
          .append('tspan')
          .text(`${formatPercent(d.data.value)}%`)
          .attr('x', 0)
          .attr('dy', '1.2em')
          .style('fill', '#666')
          .style('font-size', '8px');
      });
  }, [data, width, height]);

  return (
    <div
      ref={wrapperRef}
      style={{ position: 'relative', width: '100%', height: '100%' }}
    >
      <svg ref={ref} />
      <div
        ref={tooltipRef}
        style={{
          position: 'absolute',
          display: 'none',
          pointerEvents: 'none',
          color: '#FFFFFF',
          fontSize: '11px',
          fontWeight: 600,
          padding: '6px 10px',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
          whiteSpace: 'normal',
          minWidth: '160px',
          maxWidth: '240px',
          lineHeight: 1.3,
          zIndex: 10,
        }}
      >
        <div
          ref={tooltipArrowRef}
          style={{
            position: 'absolute',
            width: 0,
            height: 0,
            display: 'none',
          }}
        />
      </div>
    </div>
  );
};
